import React, { useCallback, useEffect, useState } from 'react';
import { Reference, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  DELETE_VISUALIZATION_PRESET_MUTATION,
  GET_MY_VISUALIZATION_PRESETS_QUERY,
  SAVE_VISUALIZATION_PRESET_MUTATION,
} from '../../gql';
import {
  VisualizationDefinition,
  VisualizationPreset,
  VisualizationSymbol,
} from '../VisualizerMapView';
import { FindPresetDialogMode } from '../FindPresetDialog';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useVocabulary } from '../../i18n';
import { FieldLabel, FormField, SurfaceTextField } from '../design-system';
import { IconButton as DesignSystemIconButton } from '../design-system';

interface VisualizationPresetDialogProps {
  mode: FindPresetDialogMode;
  open: boolean;
  name: string;
  definitions: VisualizationDefinition[];
  onClose: () => void;
  onLoad?: (preset: VisualizationPreset) => void;
  onSave?: (preset: VisualizationPreset) => void;
}

interface VisualizationPresetsQueryData {
  getMyVisualizationPresets: VisualizationPreset[];
}

interface DeleteVisualizationPresetData {
  deleteVisualizationPreset: boolean;
}

interface DeleteVisualizationPresetVariables {
  id: string;
}

const dialogPaperProps = { sx: { width: 900, maxWidth: 'calc(100vw - 32px)' } };
const contentStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
  gap: 2,
  minHeight: 380,
  maxHeight: 'calc(100vh - 180px)',
};
const listStyles = { maxHeight: 330, overflowY: 'auto' };
const deleteButtonClass = 'visualization-preset-delete';
const rowContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  borderRadius: 1,
  '&:hover': { bgcolor: 'action.hover' },
  [`&:hover .${deleteButtonClass}, &:focus-within .${deleteButtonClass}`]: {
    opacity: 1,
  },
};
const selectedRowStyles = {
  ...rowContainerStyles,
  bgcolor: 'action.selected',
  '&:hover': { bgcolor: 'action.selected' },
};
const rowButtonStyles = {
  minWidth: 0,
  flex: 1,
  justifyContent: 'flex-start',
  px: 1.5,
  py: 1.25,
};
const deleteButtonStyles = {
  flexShrink: 0,
  mr: 0.5,
  opacity: 0,
  transition: 'opacity 120ms ease',
};
const previewStyles = {
  minWidth: 0,
  borderLeft: { xs: 0, md: '1px solid' },
  borderTop: { xs: '1px solid', md: 0 },
  borderColor: 'divider',
  pl: { xs: 0, md: 2 },
  pt: { xs: 2, md: 0 },
  overflowY: 'auto',
  overflowX: 'hidden',
  maxHeight: { xs: 280, md: 380 },
  pr: 0.5,
};
const closeStyles = { position: 'absolute', right: 12, top: 10 } as const;
const errorStyles = { color: 'error.main' };
const titleStyles = { pr: 6 };
const nameInputProps = { maxLength: 100 };
const neutralInputStyles = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'sunken',
    fontSize: 13,
    '& fieldset': { borderColor: 'outline' },
    '&:hover fieldset': { borderColor: 'text.secondary' },
    '&.Mui-focused fieldset': {
      borderColor: 'text.primary',
      borderWidth: 1,
    },
  },
};
const summaryStyles = { width: '100%', minWidth: 0 };
const wrappedChipsStyles = {
  minWidth: 0,
  flex: 1,
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 0.5,
};
const compactChipsStyles = {
  ...wrappedChipsStyles,
  flexWrap: 'nowrap',
  overflow: 'hidden',
};
const summaryChipStyles = { flexShrink: 0 };
const summaryChipLabelStyles = {
  display: 'inline-flex',
  alignItems: 'center',
};
const summaryChipTitleStyles = { fontWeight: 600 };
const cancelButtonStyles = {
  color: 'text.secondary',
  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
};
const primaryActionStyles = {
  bgcolor: 'text.primary',
  color: 'background.paper',
  '&:hover': { bgcolor: 'text.secondary' },
};

const chipGroupMessages = defineMessages({
  findTypes: { id: 'find.type', defaultMessage: 'Type' },
  eras: { id: 'find.era', defaultMessage: 'Era' },
  materials: { id: 'find.material', defaultMessage: 'Material' },
  tags: { id: 'general.tag', defaultMessage: 'Tag' },
});

const chipGroups: Array<{
  key: keyof VisualizationDefinition['filter'];
  message: { id: string; defaultMessage: string };
}> = [
  { key: 'materials', message: chipGroupMessages.materials },
  { key: 'eras', message: chipGroupMessages.eras },
  { key: 'findTypes', message: chipGroupMessages.findTypes },
  { key: 'tags', message: chipGroupMessages.tags },
];

export function createVisualizationDefinitionInput(
  definition: VisualizationDefinition
): VisualizationDefinition {
  const { filter, style } = definition;

  return {
    id: definition.id,
    filter: {
      ...(filter.eras != null ? { eras: [...filter.eras] } : {}),
      ...(filter.findTypes != null ? { findTypes: [...filter.findTypes] } : {}),
      ...(filter.materials != null ? { materials: [...filter.materials] } : {}),
      ...(filter.tags != null ? { tags: [...filter.tags] } : {}),
    },
    style: {
      shape: style.shape,
      primaryColor: style.primaryColor,
      secondaryColor: style.secondaryColor ?? null,
      sizeScale: style.sizeScale,
      outlined: style.outlined,
    },
  };
}

export function VisualizationDefinitionSummary({
  definition,
  showIcon = true,
  compact = false,
}: {
  definition: VisualizationDefinition;
  showIcon?: boolean;
  compact?: boolean;
}) {
  const intl = useIntl();
  const vocabulary = useVocabulary();

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={summaryStyles}>
      {showIcon && <VisualizationSymbol style={definition.style} />}
      <Stack sx={compact ? compactChipsStyles : wrappedChipsStyles}>
        {chipGroups.map(({ key, message }) => {
          const values = definition.filter[key] || [];

          if (!values.length) {
            return null;
          }

          const displayValues = values.map((value) => {
            const vocabularyKind =
              key === 'eras'
                ? 'era'
                : key === 'findTypes'
                ? 'findType'
                : key === 'materials'
                ? 'material'
                : null;
            const displayValue = vocabularyKind
              ? vocabulary(vocabularyKind, value)
              : value;

            return displayValue;
          });

          return (
            <Chip
              key={key}
              size="small"
              label={
                <Box component="span" sx={summaryChipLabelStyles}>
                  <Box component="span" sx={summaryChipTitleStyles}>
                    {intl.formatMessage(message)}:{' '}
                  </Box>
                  {displayValues.join(' • ')}
                </Box>
              }
              sx={summaryChipStyles}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

interface MutationResultPayload {
  __typename?: string;
  errors?: Array<{ error?: string }>;
  preset?: VisualizationPreset;
}

function getMutationError(
  payload: MutationResultPayload | null,
  fallback: string
): string | null {
  if (!payload) {
    return fallback;
  }

  if (payload?.__typename !== 'ValidationError') {
    return null;
  }

  return payload.errors?.[0]?.error || fallback;
}

interface PresetRowProps {
  preset: VisualizationPreset;
  selected: boolean;
  deleting: boolean;
  onSelect: (id: string) => void;
  onDelete: (preset: VisualizationPreset) => void;
}

const PresetRow = React.memo(
  ({ preset, selected, deleting, onSelect, onDelete }: PresetRowProps) => {
    const intl = useIntl();
    const handleClick = () => onSelect(preset.id);
    const handleDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDelete(preset);
    };

    return (
      <Box sx={selected ? selectedRowStyles : rowContainerStyles}>
        <ButtonBase onClick={handleClick} sx={rowButtonStyles}>
          <Typography noWrap>{preset.name}</Typography>
        </ButtonBase>
        <DesignSystemIconButton
          className={deleteButtonClass}
          aria-label={intl.formatMessage(
            {
              id: 'visualizer.preset.deleteAria',
              defaultMessage: 'Delete {name}',
            },
            { name: preset.name }
          )}
          density="compact"
          disabled={deleting}
          onClick={handleDelete}
          sx={deleteButtonStyles}
          tone="danger"
          variant="ghost"
        >
          {deleting ? (
            <CircularProgress size={18} />
          ) : (
            <DeleteOutlineIcon fontSize="small" />
          )}
        </DesignSystemIconButton>
      </Box>
    );
  }
);

export function VisualizationPresetDialog({
  mode,
  open,
  name,
  definitions,
  onClose,
  onLoad,
  onSave,
}: VisualizationPresetDialogProps) {
  const intl = useIntl();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState(name);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { data, loading } = useQuery<VisualizationPresetsQueryData>(
    GET_MY_VISUALIZATION_PRESETS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      skip: !open,
    }
  );
  const [savePreset, { loading: saving }] = useMutation(
    SAVE_VISUALIZATION_PRESET_MUTATION,
    {
      refetchQueries: [{ query: GET_MY_VISUALIZATION_PRESETS_QUERY }],
    }
  );
  const [deletePreset] = useMutation<
    DeleteVisualizationPresetData,
    DeleteVisualizationPresetVariables
  >(DELETE_VISUALIZATION_PRESET_MUTATION, {
    update(cache, { data: mutationData }, { variables }) {
      if (!mutationData?.deleteVisualizationPreset || !variables?.id) {
        return;
      }

      cache.modify({
        fields: {
          getMyVisualizationPresets(
            existingPresets: Reference[] = [],
            { readField }
          ) {
            return existingPresets.filter(
              (preset) => readField('id', preset) !== variables.id
            );
          },
        },
      });
    },
  });
  const presets: VisualizationPreset[] = data?.getMyVisualizationPresets || [];
  const selectedPreset =
    presets.find((preset) => preset.id === selectedId) || null;
  const previewDefinitions = selectedPreset?.definitions || definitions;

  useEffect(() => {
    if (open) {
      setSelectedId(null);
      setNewName(name);
      setError(null);
      setDeletingId(null);
    }
  }, [name, open]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(event.target.value);
    setSelectedId(null);
    setError(null);
  };

  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
    setNewName('');
    setError(null);
  }, []);

  const handleDelete = useCallback(
    async (preset: VisualizationPreset) => {
      if (
        !window.confirm(
          intl.formatMessage(
            {
              id: 'visualizer.preset.deleteConfirm',
              defaultMessage:
                'Delete the visualization preset “{name}”? This cannot be undone.',
            },
            { name: preset.name }
          )
        )
      ) {
        return;
      }

      setError(null);
      setDeletingId(preset.id);

      try {
        const result = await deletePreset({ variables: { id: preset.id } });

        if (!result.data?.deleteVisualizationPreset) {
          setError(
            intl.formatMessage({
              id: 'visualizer.preset.deleteError',
              defaultMessage: 'Could not delete the preset.',
            })
          );
          return;
        }

        setSelectedId((currentId) =>
          currentId === preset.id ? null : currentId
        );
      } catch {
        setError(
          intl.formatMessage({
            id: 'visualizer.preset.deleteError',
            defaultMessage: 'Could not delete the preset.',
          })
        );
      } finally {
        setDeletingId(null);
      }
    },
    [deletePreset, intl]
  );

  const handlePrimaryAction = async () => {
    if (mode === 'load') {
      if (selectedPreset) {
        onLoad?.(selectedPreset);
        onClose();
      }
      return;
    }

    const presetName = selectedPreset?.name || newName.trim();
    if (!presetName) {
      return;
    }

    if (
      selectedPreset &&
      !window.confirm(
        intl.formatMessage(
          {
            id: 'visualizer.preset.overwriteConfirm',
            defaultMessage:
              'Overwrite the existing visualization preset “{name}”?',
          },
          { name: selectedPreset.name }
        )
      )
    ) {
      return;
    }

    try {
      const result = await savePreset({
        variables: {
          id: selectedPreset?.id || null,
          name: presetName,
          definitions: definitions.map(createVisualizationDefinitionInput),
        },
      });
      const payload = result.data?.saveVisualizationPreset || null;
      const mutationError = getMutationError(
        payload,
        intl.formatMessage({
          id: 'visualizer.preset.saveError',
          defaultMessage: 'Could not save the preset.',
        })
      );

      if (mutationError) {
        setError(mutationError);
        return;
      }

      if (!payload?.preset) {
        setError(
          intl.formatMessage({
            id: 'visualizer.preset.saveError',
            defaultMessage: 'Could not save the preset.',
          })
        );
        return;
      }

      onSave?.(payload.preset);
      onClose();
    } catch {
      setError(
        intl.formatMessage({
          id: 'visualizer.preset.saveError',
          defaultMessage: 'Could not save the preset.',
        })
      );
    }
  };

  const canSubmit =
    mode === 'load'
      ? !!selectedPreset
      : definitions.length > 0 && (!!selectedPreset || !!newName.trim());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={dialogPaperProps}
    >
      <DialogTitle sx={titleStyles}>
        {mode === 'save'
          ? intl.formatMessage({
              id: 'visualizer.preset.saveTitle',
              defaultMessage: 'Save visualization preset',
            })
          : intl.formatMessage({
              id: 'visualizer.preset.loadTitle',
              defaultMessage: 'Select visualization preset to load',
            })}
        <IconButton
          aria-label={intl.formatMessage({
            id: 'general.close',
            defaultMessage: 'Close',
          })}
          onClick={onClose}
          sx={closeStyles}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={contentStyles}>
        <Stack gap={1.5} minWidth={0}>
          {mode === 'save' && (
            <FormField>
              <FieldLabel htmlFor="visualizer-preset-new-name">
                <FormattedMessage
                  id="visualizer.preset.newName"
                  defaultMessage="New preset name"
                />
              </FieldLabel>
              <SurfaceTextField
                id="visualizer-preset-new-name"
                autoFocus
                surface="sunken"
                placeholder={intl.formatMessage({
                  id: 'visualizer.preset.namePlaceholder',
                  defaultMessage: 'Name this visualization preset',
                })}
                size="small"
                value={newName}
                onChange={handleNameChange}
                inputProps={nameInputProps}
                sx={neutralInputStyles}
              />
            </FormField>
          )}
          {mode === 'save' && <Divider />}
          <Typography fontSize={13} fontWeight={600}>
            <FormattedMessage
              id="visualizer.preset.existing"
              defaultMessage="Existing presets"
            />
          </Typography>
          <Stack gap={0.5} sx={listStyles}>
            {loading && !data ? (
              <Box py={4} textAlign="center">
                <CircularProgress size={24} />
              </Box>
            ) : presets.length ? (
              presets.map((preset) => (
                <PresetRow
                  key={preset.id}
                  preset={preset}
                  selected={selectedId === preset.id}
                  deleting={deletingId === preset.id}
                  onSelect={handleSelect}
                  onDelete={handleDelete}
                />
              ))
            ) : (
              <Typography color="text.secondary" fontSize={14}>
                <FormattedMessage
                  id="visualizer.preset.empty"
                  defaultMessage="No saved presets yet."
                />
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack gap={1.5} sx={previewStyles}>
          <Typography fontWeight={600}>
            <FormattedMessage
              id="visualizer.preset.preview"
              defaultMessage="Preview"
            />
          </Typography>
          {previewDefinitions.length ? (
            previewDefinitions.map((definition) => (
              <VisualizationDefinitionSummary
                key={definition.id}
                definition={definition}
              />
            ))
          ) : (
            <Typography color="text.secondary" fontSize={14}>
              <FormattedMessage
                id="visualizer.definition.none"
                defaultMessage="No definitions."
              />
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {error && (
          <Typography mr="auto" fontSize={13} sx={errorStyles}>
            {error}
          </Typography>
        )}
        <Button onClick={onClose} sx={cancelButtonStyles}>
          <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit || saving || deletingId != null}
          onClick={handlePrimaryAction}
          sx={primaryActionStyles}
        >
          {mode === 'save' ? (
            <FormattedMessage id="general.save" defaultMessage="Save" />
          ) : (
            <FormattedMessage id="general.load" defaultMessage="Load" />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
